// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { getProviderConfig } from '../../api/config';
import type { ProviderConfig } from '../../types';

interface AuthState {
  status: 'loading' | 'authenticated' | 'unauthenticated';
  config: ProviderConfig | null;
}

export function AuthenticatedRoute() {
  const [authState, setAuthState] = useState<AuthState>({
    status: 'loading',
    config: null,
  });

  useEffect(() => {
    getProviderConfig()
      .then((config) => {
        setAuthState({ status: 'authenticated', config });
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          window.location.href = '/login';
        } else {
          setAuthState({ status: 'unauthenticated', config: null });
        }
      });
  }, []);

  if (authState.status === 'loading') {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (authState.status === 'unauthenticated') {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600">Unable to authenticate. Please try again.</p>
          <button
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700"
            onClick={() => { window.location.href = '/login'; }}
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return <Outlet />;
}
